import natureMarketsSrc from '@assets/images/nature-markets.png';
import { Stack } from 'component-library';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { WidgetType } from '@/api/rest/resources/types/widget';
import { Report } from '@/components/widgets/o/ReportCard/ReportCard';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { CONTACT_SUPPORT_TEAM_FORM_URL } from '@/config/constants';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { isNullish } from '@/utils/booleans';

import { useMenuItems } from '../hooks/useMenuItems';

export const KnowledgePage = () => {
  const { t } = useTranslation();
  const menuItems = useMenuItems();

  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();

  const customWidgetSlotsConfiguration = useActiveWidgetSlotConfiguration(
    'default_template_knowledge',
    WidgetType.t_page,
  );

  const widgetSlotsConfiguration = customWidgetSlotsConfiguration ?? defaultWidgetConfiguration;

  return (
    <SignedInPageLayout
      items={menuItems}
      navBarProps={{
        title: t('navbar.knowledge'),
      }}
    >
      <Stack spacing={10}>
        <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
      </Stack>
    </SignedInPageLayout>
  );
};

enum KnowledgeReportNames {
  natureMarkets = 'natureMarkets',
}

const useDefaultWidgetConfiguration = () => {
  const { t } = useTranslation();
  const [activeReport, setActiveReport] = useState<Report | null>(null);

  const reports = [
    {
      id: KnowledgeReportNames.natureMarkets as string,
      report_name: t('knowledgeBase.reports.natureMarkets.header'),
      report_date: new Date().toISOString(),
      created_at: new Date().toISOString(),
      file_download_url: '',
      thumbnail_url: natureMarketsSrc,
      to: CONTACT_SUPPORT_TEAM_FORM_URL,
    },
  ] as Report[];

  const onClickReport = (report: Report) => {
    setActiveReport(report);
  };

  return {
    name: 'default_template_knowledge',
    type: WidgetType.t_page,
    widgets: [
      {
        name: 'default_knowledge_cards',
        type: WidgetType.o_c_knowledge_cards,
        visible: isNullish(activeReport),
        relatedData: {
          articles: reports,
          onArticleClick: onClickReport,
        },
      },
      {
        name: 'default_knowledge_report',
        type: WidgetType.o_card,
        visible: !isNullish(activeReport),
        relatedData: {
          header: !isNullish(activeReport) && (
            <Trans i18nKey={`knowledgeBase.reports.${activeReport.id as keyof typeof KnowledgeReportNames}.header`} />
          ),
          body: !isNullish(activeReport) && (
            <Stack className='gap-8'>
              {!isNullish(activeReport.thumbnail_url) && (
                <img
                  src={activeReport.thumbnail_url}
                  alt={`Thumbnail for ${activeReport.report_name}`}
                  className='max-h-full max-w-full object-contain rounded-lg mb-4'
                />
              )}
              <Trans
                i18nKey={`knowledgeBase.reports.${activeReport.id as keyof typeof KnowledgeReportNames}.body`}
                components={{
                  h3: <span className='typography-h3 text-text-primary whitespace-pre-wrap' />,
                }}
              />
            </Stack>
          ),
          footer: !isNullish(activeReport) && (
            <Trans
              i18nKey={`knowledgeBase.reports.${activeReport.id as keyof typeof KnowledgeReportNames}.footer`}
              components={{
                /* eslint-disable-next-line jsx-a11y/anchor-has-content */
                Link: <a className='underline' href={activeReport.to} target='_blank' rel='noreferrer' />,
              }}
            />
          ),
          styles: {
            root: 'border-none w-full px-4 whitespace-pre-wrap',
            header: 'border-none typography-h2 text-text-primary px-0 font-semibold',
            body: 'border border-none typography-body1 text-text-secondary',
            footer:
              'border border-none typography-body1Semibold text-text-primary bg-neutral-black-4 rounded-2xl m-4 mt-8',
          },
        },
      },
    ],
  };
};
